/**
 * ErrorBoundary — class component React pour capturer les erreurs de rendu.
 * Affiche l'erreur exacte au lieu d'une page blanche.
 * Diagnostic uniquement — à remplacer par un UI propre en prod.
 */
/**
 * ⚠⚠ `#839` — **UNE CLASSE REACT NE PEUT PAS APPELER UN HOOK.** *Et c'est le
 *   dernier filet quand tout le reste a échoué : il rend le FRANÇAIS,
 *   toujours. `trFr` est la lecture pure du catalogue — pas de contexte,
 *   pas de bascule. **Un filet qui dépendrait de i18next tomberait avec
 *   lui**, et l'écran redeviendrait blanc.*
 */
import { trFr } from '@/lib/i18n';
import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          padding: '2rem',
          fontFamily: 'monospace',
          background: '#fff',
          color: '#c00',
          border: '2px solid #c00',
          borderRadius: '8px',
          margin: '2rem',
        }}>
          <h2 style={{ marginTop: 0 }}>{trFr('commun.erreurReact')}</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px' }}>
            {this.state.error?.message}
          </pre>
          <details style={{ marginTop: '1rem' }}>
            {/* ⚠ `#800` — `Stack trace` est un invariant : FR == EN. */}
            <summary>Stack trace</summary>
            <pre style={{ fontSize: '11px', whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
              {this.state.error?.stack}
            </pre>
            <pre style={{ fontSize: '11px', whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}
